import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Sparkles, X } from 'lucide-react';
import type { TourConfig } from '@/shared/services/api/SettingsService';
import DOMPurify from 'dompurify';

interface TourInitialModalProps {
  config: TourConfig;
  onStart: () => void;
  onClose: () => void;
}

export const TourInitialModal = ({ config, onStart, onClose }: TourInitialModalProps) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0] as 'pt' | 'en';
  
  // Pega valores traduzidos do initialModal
  const title = config.initialModal.title[currentLang] || config.initialModal.title.pt;
  const contentHtml = config.initialModal.contentHtml[currentLang] || config.initialModal.contentHtml.pt;
  const startButtonText = config.initialModal.buttons.start[currentLang] || config.initialModal.buttons.start.pt;
  const skipButtonText = config.initialModal.buttons.skip[currentLang] || config.initialModal.buttons.skip.pt;
  
  // Sanitiza HTML para segurança (permite iframe para vídeos)
  const sanitizedHtml = useMemo(() => {
    return DOMPurify.sanitize(contentHtml, {
      ALLOWED_TAGS: ['p', 'div', 'span', 'ul', 'li', 'br', 'strong', 'em', 'b', 'i', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'img', 'iframe', 'video', 'source'],
      ALLOWED_ATTR: ['class', 'href', 'target', 'src', 'alt', 'title', 'frameborder', 'allow', 'allowfullscreen', 'width', 'height', 'controls', 'preload', 'type'],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|youtube):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
    });
  }, [contentHtml]);

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            <DialogTitle className="text-2xl">
              {title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="py-4">
          {/* Conteúdo HTML customizável (pode incluir vídeos iframe/video) */}
          <div 
            className="tour-initial-content"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            {skipButtonText}
          </Button>
          {/* Sempre habilitado: o modal só é aberto por ação explícita do usuário
              (boas-vindas no primeiro acesso ou "Tour Guiado" na navbar), então o
              botão deve iniciar/reiniciar o tour mesmo se já concluído (AB#83990). */}
          <Button onClick={onStart}>
            <Sparkles className="h-4 w-4 mr-2" />
            {startButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
